/**
 * Read-only rendering of one stored template cell, monday-board style.
 * Values are the stored { id, value, text, type } shape, decoded with
 * parseColumnValue so display always matches what apply will send.
 */
import { Avatar, Tooltip } from 'monday-ui-react-core';
import { Check } from 'monday-ui-react-core/icons';
import { AlertCircle, Loader2, Paperclip, Zap } from 'lucide-react';
import React from 'react';
import { canonicalType } from '../../Config';
import { RatingDisplay } from '../RatingSymbols';
import { parseColumnValue } from '../../lib/columnValues';
import { applyBaseDate, describeRule, describeTimeline, formatDate, resolveDate, resolveTimeline } from '../../lib/dynamicValues';

const AVATAR_COLORS = [
  '#0073ea', '#a25ddc', '#00c875', '#e2445c', '#fdab3d',
  '#579bfc', '#66ccff', '#ff642e', '#7f5347', '#c4c4c4',
];

const initialsOf = (name) =>
  String(name || '?')
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

const avatarColor = (id) => AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];

const EmptyDash = () => <span className="spt-empty-dash">—</span>;

/** A dynamic date or timeline rule: its words behind a bolt, worked out at apply. */
const DynamicValue = ({ title, children }) => (
  <span className="spt-dyn-value" title={title}>
    <Zap aria-label="Dynamic" />
    <span>{children}</span>
  </span>
);

/**
 * @param column   { id, title, type, settings? } — settings (parsed by columnSettingsOf)
 *                 are present when the board is known (editor); without them,
 *                 status labels render in a neutral colour.
 * @param rawValue stored column_value ({id,value,text,type}) or undefined
 */
export function statusColorFor(column, draft) {
  const labels = column.settings && column.settings.labels;
  if (!labels || !draft) return null;
  const hit = labels.find((l) => l.index === draft.index);
  return hit ? hit.color : null;
}

/** Progress of several uploads together, by size: 1 MB at 50% and 3 MB at 0% is 13%. */
export function uploadPercent(uploads) {
  const total = uploads.reduce((n, u) => n + (Number(u.size) || 0), 0);
  if (!total) return Math.round(uploads.reduce((n, u) => n + (u.pct || 0), 0) / Math.max(1, uploads.length));
  return Math.round(uploads.reduce((n, u) => n + (u.pct || 0) * (Number(u.size) || 0), 0) / total);
}

export default function CellContent({ column, rawValue, uploads }) {
  const draft = rawValue ? parseColumnValue(rawValue, column) : null;

  switch (canonicalType(column.type)) {
    case 'status': {
      // Status renders at the <td> level (solid fill) — see SubitemsTable.
      // This branch only handles the inner label text.
      return draft ? <span>{draft.label}</span> : null;
    }
    case 'people': {
      if (!draft || !draft.persons.length) return <EmptyDash />;
      return (
        <span className="spt-avatars">
          {draft.persons.map((p) => (
            <Tooltip key={p.id} content={p.name || String(p.id)}>
              <Avatar
                size={Avatar.sizes.SMALL}
                type={Avatar.types.TEXT}
                text={initialsOf(p.name)}
                backgroundColor={undefined}
                customBackgroundColor={avatarColor(p.id)}
                ariaLabel={p.name || String(p.id)}
              />
            </Tooltip>
          ))}
        </span>
      );
    }
    case 'date': {
      if (draft && typeof draft === 'object' && draft.dynamic) {
        // a rule, as plain text behind a bolt; the title says where it lands today
        const lands = resolveDate(draft.dynamic, applyBaseDate(Intl.DateTimeFormat().resolvedOptions().timeZone));
        return (
          <DynamicValue title={lands ? `Resolves to ${formatDate(lands)} if applied today` : undefined}>
            {describeRule(draft.dynamic)}
          </DynamicValue>
        );
      }
      // the same "Oct 23, 2026" as the date editor's calendar and the dynamic previews
      return draft ? <span>{formatDate(draft)}</span> : <EmptyDash />;
    }
    case 'timeline': {
      if (draft && typeof draft === 'object' && draft.dynamic) {
        // a rule, as plain text behind a bolt; the title says where it lands today
        const t = resolveTimeline(draft.dynamic, applyBaseDate(Intl.DateTimeFormat().resolvedOptions().timeZone));
        return (
          <DynamicValue title={t ? `Resolves to ${formatDate(t.from)} – ${formatDate(t.to)} if applied today` : undefined}>
            {describeTimeline(draft.dynamic)}
          </DynamicValue>
        );
      }
      return draft ? <span className="spt-timeline-pill">{`${formatDate(draft.from)} – ${formatDate(draft.to)}`}</span> : <EmptyDash />;
    }
    case 'dropdown':
      return draft ? <span>{draft.labels.join(', ')}</span> : <EmptyDash />;
    case 'rating':
      // always render the column's own symbol/limit, even when unrated
      return <RatingDisplay column={column} value={draft} />;
    case 'checkbox':
      return draft ? <Check size="16" /> : <EmptyDash />;
    case 'link':
      return draft ? <span className="spt-link">{draft.text || draft.url}</span> : <EmptyDash />;
    case 'file': {
      const names = draft && draft.files ? draft.files.map((f) => f.name).filter(Boolean) : [];
      // uploads still running for this cell (lib/cellUploads), shown on one line beside its files
      const running = (uploads || []).filter((u) => !u.error);
      const failed = (uploads || []).length - running.length;
      if (!names.length && !running.length && !failed) return <EmptyDash />;
      return (
        <span className="spt-cell-filesline">
          {names.length > 0 && (
            <span className="spt-cell-files" title={names.join(', ')}>
              <Paperclip aria-hidden="true" />
              <span>{names.length === 1 ? names[0] : `${names.length} files`}</span>
            </span>
          )}
          {running.length > 0 && (
            <span className="spt-cell-files spt-cell-uploading" role="status" title={running.map((u) => u.name).join(', ')}>
              <Loader2 className="animate-spin" aria-hidden="true" />
              <span>
                {running.length === 1 && !names.length ? `Uploading ${running[0].name}` : `${running.length} uploading`} · {uploadPercent(running)}%
              </span>
            </span>
          )}
          {failed > 0 && (
            <span className="spt-cell-files spt-cell-upload-failed" role="alert">
              <AlertCircle aria-hidden="true" />
              <span>{failed} failed</span>
            </span>
          )}
        </span>
      );
    }
    // numbers, text, long_text and any other plain value
    default:
      return draft !== null && draft !== undefined && draft !== '' ? (
        <span>{String(draft)}</span>
      ) : (
        <EmptyDash />
      );
  }
}
